import {
    differenceInDays,
    differenceInSeconds,
    eachDayOfInterval,
    endOfDay,
    endOfMonth,
    isMonday,
    isSunday,
    nextMonday,
    nextSunday,
    startOfDay,
    startOfMonth,
} from "date-fns";

export const months = [
    {name:'Gennaio', label: 'Gen'},
    {name:'Febbraio', label: 'Feb'},
    {name:'Marzo', label: 'Mar'},
    {name:'Aprile', label: 'Apr'},
    {name:'Maggio', label: 'Mag'},
    {name:'Giugno', label: 'Giu'},
    {name:'Luglio', label: 'Lug'},
    {name:'Agosto', label: 'Ago'},
    {name:'Settembre', label: 'Set'},
    {name:'Ottobre', label: 'Ott'},
    {name:'Novembre', label: 'Nov'},
    {name:'Dicembre', label: 'Dic'}
]
export const dayLabels = ['D', 'L', 'M', 'M', 'G', 'V', 'S'];
export const dayOfWeek = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];

/*
 Chunk the given array in chunk of the given size
 */
export const chunk = (arr, size) => {
    return Array.from({length: Math.ceil(arr.length / size)}, (v, i) =>
        arr.slice(i * size, i * size + size)
    );
}

/*
 Return an array of Date that correspond to the set of days visible
 in the month vertical view
 */
export const monthVerticalVisibleDays = (monthNumber, year) => {
    const [firstVisibleDay, lastVisibleDay] = monthVerticalDayRange(monthNumber, year);
    return eachDayOfInterval({start: firstVisibleDay, end: lastVisibleDay});
}

/*
 * Return the first and the last day to show in the month vertical view
 * for the given month and year.
 * The first day is the first monday of the month.
 * The last day is the first sunday after the last day of the month (if not sunday).
 */
export const monthVerticalDayRange = (monthNumber, year) => {
    const monthStart = new Date(year, monthNumber, 1);
    const monthEnd = endOfMonth(monthStart);
    const firstDay = isMonday(monthStart) ? monthStart : nextMonday(monthStart);
    const lastDay = isSunday(monthEnd) ? monthEnd : nextSunday(monthEnd);

    return [firstDay, lastDay];
}

export const monthRange = (monthNumber, year) => {
    const monthStart = startOfDay(new Date(year, monthNumber, 1));
    const monthEnd = endOfDay(endOfMonth(monthStart));

    return [monthStart, monthEnd];
}

/*
 Return an array of Date that correspond to the set of days visible
 in the month of the timeline view
 */
export const timeLineVisibleDays = (monthNumber, year) => {
    const monthStart = new Date(year, monthNumber, 1);
    const monthEnd = endOfMonth(monthStart);

    return eachDayOfInterval({start: monthStart, end: monthEnd});
}

/*
 Get the contents that are visible in a given temporal interval
 */
export const getVisibleContents = (contents, startDate, endDate) => {
    return contents.filter(d => d.endDate >= startOfDay(startDate) && d.startDate <= endOfDay(endDate));
}

/*
 * Given an array of events sort them from the oldest to the recent by looking
 * at the startDate attribute.
 */
export const sortEvents = (events) => {
    return events.sort((a, b) => a.startDate - b.startDate );
}

/*
 Given an array of events (with startDate and endDate attributes) an interval (startDate, endDate)
 return an array of segments. A segment represents an empty space, an event bar to be printed in the Planner
 or a portion of an event to be printed to the Planner.
 The segments indicate the fractional length of the events with respect to the given interval, when the
 associated segment event is null, it represents an empty space.
 The segments have also additional fields: leftCut and rightCut that indicate whether the
 event has been cut on the left or on the right and isCentralSegment that indicates whether the
 segment contain the central time instant of the event.

 Example of segment:
   {
     segmentId: string,
     lengthFraction: float,
     event: {...},
     leftCut: boolean,
     rightCut: boolean,
     isCentralSegment
   }

 When cutRightOverflow is set to false the last segment is allowed to go outside the given interval of date.
 The total length of all the returned segments will be greater than 100%.

 Assumption: the events have to be sequential and not overlapped otherwise they are cropped and forced to be
 sequential.
 */
export const getSegments = (event, startDate, endDate, cutRightOverflow=true) => {
    const totalSeconds = differenceInSeconds(endDate, startDate);

    let segments = []
    let currentDate = startDate;

    for (let i = 0; i < event.length; i++) {
        // Crop
        const segmentStartDate = event[i].startDate < currentDate ? currentDate : event[i].startDate;
        let segmentEndDate = event[i].endDate;
        if(cutRightOverflow){
            segmentEndDate = event[i].endDate > endDate ? endDate : event[i].endDate;
        }

        const leftCut = event[i].startDate < currentDate;
        const rightCut = cutRightOverflow && event[i].endDate > endDate;

        // Throw an exception if segmentStartDate > segmentEndDate that might happen with overlapped/unsorted events
        if (segmentStartDate > segmentEndDate){
            throw new Error("Planner events are either completely overlapped or not sorted");
        }

        // Add an offset
        if (segmentStartDate > currentDate) {
            const offset = differenceInSeconds(segmentStartDate, currentDate) / totalSeconds;
            segments.push({segmentId: `${segmentStartDate.getTime()}-${offset}`, lengthFraction: offset})
        }

        // Add the event
        const centralDate = (event[i].endDate.getTime() + event[i].startDate.getTime()) / 2;
        const length = differenceInSeconds(segmentEndDate, segmentStartDate) / totalSeconds;
        segments.push({
            segmentId: event[i].id,
            lengthFraction: length,
            event: event[i],
            leftCut: leftCut,
            rightCut: rightCut,
            isCentralSegment: centralDate >= segmentStartDate.getTime() && centralDate < segmentEndDate.getTime(),
        });
        currentDate = segmentEndDate;
    }
    return segments;
}

/*
 Check whether a Date is in the first seven days of its month
 */
export const inFirstSevenDaysOfMonth = (date) => {
    return date.getDate() <= 7;
}

/*
 Given a month (as a Date object) return the number of mondays within it
 */
export const getNumberOfMondays = (monthDate) => {
    const month = startOfMonth(monthDate);
    const firstMonday = isMonday(month) ? month : nextMonday(month)
    return  Math.floor(differenceInDays(endOfMonth(month), firstMonday) / 7) + 1
}
